<template>
  <div class="srt-map-wrap">
    <div ref="inner" class="srt-map-inner">
    <div class="srt-map" :style="{ width: mapWidth + 'px', height: mapHeight + 'px', marginLeft: offsetX + 'px' }">
      <svg class="srt-lines" :width="lay.width" :height="mapHeight" aria-hidden="true">
        <path v-for="g in lay.ghosts" :key="'g-' + g.s.key" :d="g.d" class="srt-line srt-line--ghost" :class="{ 'is-dim': dim(g.s) }"></path>
        <path v-for="l in lay.lines" :key="'l-' + l.s.key" :d="l.d" class="srt-line" :class="['srt-line--' + l.s.kind, { 'is-dim': dim(l.s), 'is-focus': lit(l.s) }]"></path>
        <path v-for="b in boxes" :key="'st-' + b.s.key" :d="'M ' + lay.stubX + ' ' + b.ty + ' L ' + lay.outX + ' ' + b.ty" class="srt-stub" :class="['srt-stub--' + b.st.tone, { 'is-dim': dim(b.s) }]"></path>
      </svg>

      <div class="srt-colhead" style="left: 0">Encoders · streams</div>
      <div class="srt-colhead srt-colhead--center" :style="{ left: lay.pillLeft + 'px', width: lay.pillWidth + 'px' }">Channel</div>
      <div class="srt-colhead" :style="{ left: lay.oriX + 'px' }">Origins</div>
      <div class="srt-colhead" :style="{ left: lay.outX + 'px' }">Stream Monitoring - Origin Output</div>

      <div v-for="ec in lay.encoders" :key="'e-' + ec.e.name" class="srt-card srt-card--enc"
           :class="{ 'is-dim': encDim(ec.e), 'is-pinned': isPinned('enc', ec.e.name) }"
           :style="{ top: ec.top + 'px', height: ec.h + 'px', width: geo.ENC_W + 'px' }"
           @mouseleave="$emit('hover', null)">
        <button type="button" class="srt-card__head" :aria-pressed="isPinned('enc', ec.e.name)"
                @mouseenter="$emit('hover', { k: 'enc', id: ec.e.name })" @click="$emit('pin', { k: 'enc', id: ec.e.name })">
          <span class="srt-card__ico">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="7" rx="2"/><rect x="3" y="13" width="18" height="7" rx="2"/><path d="M7 7.5h.01M7 16.5h.01"/></svg>
          </span>
          <span class="srt-card__titles">
            <span class="srt-card__name srt-mono">{{ ec.e.name }}</span>
            <span class="srt-card__sub" :class="{ 'is-bad': encSub(ec.e).bad }">{{ encSub(ec.e).text }}</span>
          </span>
        </button>
        <button v-for="r in ec.rows" :key="r.s.key" type="button" class="srt-srow" :class="['srt-k--' + r.s.kind, { 'is-dim': dim(r.s), 'is-sel': isPinned('stream', r.s.key) }]"
                :style="{ top: r.top + 'px' }" :title="r.s.name"
                @mouseenter="$emit('hover', { k: 'stream', id: r.s.key })" @click="$emit('pin', { k: 'stream', id: r.s.key })">
          <span class="srt-dot"></span>
          <span class="srt-srow__text">
            <span class="srt-srow__name">{{ r.s.name }}</span>
            <span class="srt-srow__meta"><span class="srt-mono">#{{ r.s.streamId }}</span> · <span class="srt-k-text">{{ streamLabel(r.s) }}</span></span>
          </span>
          <span class="srt-srow__rate srt-mono">{{ r.s.running ? bitrate(r.s) : '' }}</span>
          <span class="srt-port srt-port--out"></span>
        </button>
      </div>

      <button v-for="p in lay.pills" :key="'p-' + p.s.key" type="button" class="srt-pill" :class="['srt-pill--' + p.s.kind, { 'is-dim': dim(p.s) }]"
              :style="{ left: p.left + 'px', top: p.top + 'px', width: p.width + 'px' }" :title="pillTitle(p.s)"
              @mouseenter="$emit('hover', { k: 'stream', id: p.s.key })" @mouseleave="$emit('hover', null)" @click="$emit('pin', { k: 'stream', id: p.s.key })">
        <svg v-if="p.s.err" viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l9.5 17h-19z"/><path d="M12 10v4M12 17.5h.01"/></svg>
        {{ p.label }}
      </button>

      <div v-for="o in lay.origins" :key="'o-' + o.name" class="srt-card srt-card--ori"
           :class="{ 'is-dim': oriDim(o), 'is-pinned': isPinned('ori', o.name) }"
           :style="{ top: o.top + 'px', height: o.h + 'px', left: lay.oriX + 'px', width: geo.ORI_W + 'px' }"
           @mouseleave="$emit('hover', null)">
        <button type="button" class="srt-card__head" :aria-pressed="isPinned('ori', o.name)"
                @mouseenter="$emit('hover', { k: 'ori', id: o.name })" @click="$emit('pin', { k: 'ori', id: o.name })">
          <span class="srt-card__ico srt-card__ico--ori">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 9.5a4 4 0 0 1-.5 8.5z"/></svg>
          </span>
          <span class="srt-card__titles">
            <span class="srt-card__name srt-mono">{{ o.name }}</span>
            <span class="srt-card__sub" :class="{ 'is-bad': oriSub(o).bad }">{{ oriSub(o).text }}</span>
          </span>
        </button>
        <button v-for="it in o.items" :key="it.type + it.s.key" type="button" class="srt-orow"
                :class="[it.type === 'exp' ? 'srt-orow--exp' : 'srt-k--' + it.s.kind, { 'is-dim': dim(it.s), 'is-sel': isPinned('stream', it.s.key) }]"
                :style="{ top: it.top + 'px' }"
                @mouseenter="$emit('hover', { k: 'stream', id: it.s.key })" @click="$emit('pin', { k: 'stream', id: it.s.key })">
          <span class="srt-port srt-port--in"></span>
          <span class="srt-orow__text">
            <span class="srt-orow__ch">
              <span class="srt-mono srt-orow__chname">{{ it.type === 'exp' ? it.s.expected.channel : it.s.dest.channel }}</span>
              <span class="srt-mono srt-orow__app">{{ it.type === 'exp' ? it.s.expected.app : it.s.dest.app }}</span>
            </span>
            <span class="srt-orow__sub">{{ originRowSub(it) }}</span>
          </span>
          <span class="srt-orow__label srt-k-text">{{ originRowLabel(it) }}</span>
        </button>
        <div v-if="!o.items.length" class="srt-orow srt-orow--empty" :style="{ top: (geo.HDR + 1) + 'px' }">No channels arriving</div>
      </div>

      <button v-for="b in boxes" :key="'out-' + b.s.key" type="button" class="srt-out" :class="['srt-out--' + b.st.tone, { 'is-dim': dim(b.s), 'is-open': openKey === b.s.key }]"
              :style="{ left: lay.outX + 'px', top: (b.ty - 20) + 'px', width: geo.OUT_W + 'px' }" :aria-expanded="openKey === b.s.key" title="Show all variants"
              @mouseenter="$emit('hover', { k: 'stream', id: b.s.key })" @mouseleave="$emit('hover', null)" @click="toggleOutput(b.s.key)" :data-out-key="b.s.key">
        <span class="srt-out__dot"></span>
        <span class="srt-out__text">
          <span class="srt-out__title">{{ b.st.title }}</span>
          <span class="srt-out__detail">{{ b.st.detail }}</span>
        </span>
        <svg class="srt-out__chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3l5 5-5 5"/></svg>
      </button>

      <!-- Rendered on <body>, fixed to the screen next to the clicked box, so no page container can clip it. -->
      <teleport to="body">
        <div v-if="detail" class="srt-page srt-popover">
          <section ref="card" class="srt-outcard" :class="'srt-outcard--' + detail.st.tone" :style="cardStyle" aria-label="Origin output details">
            <div class="srt-outcard__head">
              <span class="srt-out__dot"></span>
              <span class="srt-outcard__titles">
                <span class="srt-outcard__title">{{ detail.st.title }} · <span class="srt-mono">{{ detail.s.expected.channel }}</span> on <span class="srt-mono">{{ detail.s.expected.origin }}</span></span>
                <span class="srt-outcard__summary">{{ detail.summary }}</span>
              </span>
              <button type="button" class="srt-outcard__close" aria-label="Close" @click="openKey = null">
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
              </button>
            </div>
            <div class="srt-outcard__meta">
              <span v-for="m in detail.meta" :key="m.k"><span class="srt-outcard__k">{{ m.k }}</span><span :class="{ 'srt-mono': m.mono }">{{ m.v }}</span></span>
            </div>
            <div v-if="detail.entry && detail.entry.phase === 'error'" class="srt-outcard__error">
              <span class="srt-outcard__k srt-outcard__k--bad">Monitor error</span>
              <span class="srt-outcard__errtext srt-mono">{{ detail.entry.error || 'Unknown error' }}</span>
              <span class="srt-muted">The monitor retries on its next calendar poll. A "master playlist" error means the origin has no stream for this channel.</span>
            </div>
            <div v-else-if="!detail.entry" class="srt-outcard__note">{{ detail.note }}</div>
            <template v-else-if="detail.variants.length">
              <div class="srt-vrow srt-vrow--head"><span>Variant</span><span>Status</span><span>Picture</span><span>Frozen</span><span>Last checked</span></div>
              <div v-for="v in detail.variants" :key="v.name" class="srt-vrow" :class="'srt-vrow--' + v.tone">
                <span class="srt-mono srt-vrow__name">{{ v.name }}</span>
                <span class="srt-vrow__status"><span class="srt-vrow__dot"></span>{{ v.status }}</span>
                <span :class="{ 'srt-vrow__warn': v.pictureBad }">{{ v.picture }}</span>
                <span :class="{ 'srt-vrow__warn': v.frozenBad }">{{ v.frozen }}</span>
                <span class="srt-mono">{{ v.checked }}</span>
              </div>
            </template>
            <div v-else class="srt-outcard__note">The monitor has started but no variant has been checked yet.</div>
            <div class="srt-outcard__foot">From the stream monitor · picture and frozen are the latest checked frame</div>
          </section>
        </div>
      </teleport>
    </div>
    </div>
  </div>
</template>

<script>
import { GEO, FIXED_W, layoutRoutes, streamMatches, outputStatus, variantProblem, secondsAgo } from '@/utils/stream-routing';

// Gap the lines curve through: half of the free width, within these bounds.
const CURVE_MIN = 160;
const CURVE_MAX = 420;
const CARD_W = 560;
import { formatBitrate } from '@/utils/bapi-functions';

export default {
  name: 'StreamRoutingMap',
  props: {
    model: { type: Object, required: true },
    focus: { type: Object, default: null },
    pinned: { type: Object, default: null },
    onlyErrors: { type: Boolean, default: false },
    // GET /monitor/status entries by cal_channel_id, and { loaded, error } for the call itself
    monitorById: { type: Object, default: () => ({}) },
    monitor: { type: Object, default: () => ({ loaded: false, error: '' }) },
    nowMs: { type: Number, default: () => Date.now() }
  },
  emits: ['hover', 'pin'],
  data() {
    return { geo: GEO, openKey: null, avail: 0, cardStyle: {} };
  },
  computed: {
    curve() {
      const free = this.avail - FIXED_W;
      return Math.min(CURVE_MAX, Math.max(CURVE_MIN, Math.round(free / 2)));
    },
    lay() {
      return layoutRoutes(this.model, this.curve);
    },
    mapWidth() {
      return this.lay.width;
    },
    offsetX() {
      return Math.max(0, Math.floor((this.avail - this.lay.width) / 2));
    },
    boxes() {
      return this.lay.outs.map(o => ({
        ...o,
        st: outputStatus(this.monitorById[o.s.plan.calChannelId], this.monitor, this.nowMs)
      }));
    },
    detail() {
      const b = this.boxes.find(x => x.s.key === this.openKey);
      if (!b) return null;
      const entry = this.monitorById[b.s.plan.calChannelId] || null;
      const at = (iso) => {
        const ago = secondsAgo(iso, this.nowMs);
        return ago === null ? '-' : new Date(Date.parse(iso)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' (' + ago + 's ago)';
      };
      const variants = ((entry && entry.variants) || []).map((v) => {
        const p = variantProblem(v);
        return {
          name: v.name,
          tone: !v.healthy ? 'bad' : (p ? 'warn' : 'ok'),
          status: !v.healthy ? 'Unreachable' : (p ? 'Problem' : 'OK'),
          picture: v.classification == null ? '-' : ({ normal: 'Normal', black: 'Black screen', white: 'White screen' }[v.classification] || v.classification),
          pictureBad: !!v.classification && v.classification !== 'normal',
          frozen: v.is_frozen == null ? '-' : (v.is_frozen ? 'Frozen' : 'No'),
          frozenBad: !!v.is_frozen,
          checked: at(v.last_checked_at)
        };
      });
      let summary = 'No entry in the stream monitor yet';
      if (entry && entry.phase === 'error') summary = 'Monitoring could not start';
      else if (entry) summary = `${variants.filter(v => v.tone === 'ok').length} of ${variants.length} variants OK`;
      if (this.monitor.error) summary = 'Stream monitor not reachable';
      // Opens over the map, just under the clicked box, right aligned with the output column.
      return {
        s: b.s, st: b.st, entry, variants, summary,
        meta: [
          { k: 'Match', v: b.s.match.title },
          { k: 'Source', v: `${b.s.encoder} #${b.s.streamId}`, mono: true },
          { k: 'Phase', v: entry ? entry.phase : '-', mono: true },
          { k: 'Last cycle', v: entry ? at(entry.last_cycle_at) : '-', mono: true }
        ],
        note: this.monitor.error
          ? 'The stream monitor could not be read: ' + this.monitor.error
          : 'This channel has no entry in the stream monitor yet. It starts checking 2 minutes after the feed window opens and picks up new channels on its next calendar poll, so it can take up to about 7 minutes.',
        height: variants.length ? 170 + variants.length * 37 : 240
      };
    },
    // The variant card floats over the map and the page below it: it never changes the layout.
    mapHeight() {
      return this.lay.height;
    },
    filtering() {
      return !!this.focus || this.onlyErrors;
    }
  },
  mounted() {
    this._onDocClick = (e) => {
      if (!this.openKey) return;
      const card = this.$refs.card;
      if (card && card.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.srt-out')) return;
      this.openKey = null;
    };
    this._onKey = (e) => { if (e.key === 'Escape') this.openKey = null; };
    this._onMove = () => { if (this.openKey) this.placeCard(); };
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
    window.addEventListener('scroll', this._onMove, true);
    window.addEventListener('resize', this._onMove);
    this.measure();
    if (window.ResizeObserver) {
      this._ro = new ResizeObserver(() => this.measure());
      this._ro.observe(this.$refs.inner);
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('scroll', this._onMove, true);
    window.removeEventListener('resize', this._onMove);
    if (this._ro) this._ro.disconnect();
  },
  methods: {
    measure() {
      this.avail = this.$refs.inner ? Math.floor(this.$refs.inner.clientWidth) : 0;
    },
    toggleOutput(key) {
      const open = this.openKey === key;
      this.openKey = open ? null : key;
      if (!open && !this.isPinned('stream', key)) this.$emit('pin', { k: 'stream', id: key });
      if (!open) this.placeCard();
    },
    // Fixed position under the clicked box (above it if there is more room there), right aligned
    // with it and kept inside the window. Taller than the room left: the card scrolls inside.
    placeCard() {
      const box = this.$el.querySelector(`[data-out-key="${this.openKey}"]`);
      if (!box) return;
      const r = box.getBoundingClientRect();
      const margin = 12;
      const left = Math.max(margin, Math.min(r.right - CARD_W, window.innerWidth - CARD_W - margin));
      const below = window.innerHeight - r.bottom - margin - 6;
      const above = r.top - margin - 6;
      if (below >= 360 || below >= above) {
        this.cardStyle = { left: left + 'px', top: (r.bottom + 6) + 'px', maxHeight: below + 'px' };
      } else {
        this.cardStyle = { left: left + 'px', bottom: (window.innerHeight - r.top + 6) + 'px', maxHeight: above + 'px' };
      }
    },
    passes(s) {
      return streamMatches(s, { focus: this.focus, onlyErrors: this.onlyErrors });
    },
    dim(s) {
      return this.filtering && !this.passes(s);
    },
    lit(s) {
      return this.filtering && this.passes(s);
    },
    isPinned(k, id) {
      return !!this.pinned && this.pinned.k === k && this.pinned.id === id;
    },
    encDim(e) {
      if (!this.filtering) return false;
      if (this.focus && this.focus.k === 'enc' && this.focus.id === e.name) return false;
      return !e.streams.some(s => this.passes(s));
    },
    oriDim(o) {
      if (!this.filtering) return false;
      if (this.focus && this.focus.k === 'ori' && this.focus.id === o.name) return false;
      return !o.items.some(it => this.passes(it.s));
    },
    encSub(e) {
      if (e.unreachable) return { text: 'Not reachable, status unknown', bad: true };
      if (e.pending) return { text: 'Loading status...', bad: false };
      const running = e.streams.filter(s => s.running).length;
      const errs = e.streams.filter(s => s.err).length;
      const down = e.streams.filter(s => s.kind === 'down').length;
      const bits = [`${e.streams.length} ${e.streams.length === 1 ? 'stream' : 'streams'}`, `${running} running`];
      if (down) bits.push(`${down} down`);
      if (errs) bits.push(`${errs} mismatch`);
      return { text: bits.join(' · '), bad: errs > 0 || down > 0 };
    },
    streamLabel(s) {
      const league = s.match ? ' · ' + s.match.title : '';
      switch (s.kind) {
        case 'live': return 'Live' + league;
        case 'checking': return (s.output && s.output.failed ? 'Live · output not read' : 'Live · reading output') + league;
        case 'mismatch': return s.err === 'origin' ? 'Live · wrong origin' : 'Live · wrong channel';
        case 'nooutput': return 'Live · no output URL';
        case 'down': return 'Down · not running' + league;
        default: return 'No status' + league;
      }
    },
    bitrate(s) {
      return s.stat ? formatBitrate(s.stat.bitrate) : '';
    },
    pillTitle(s) {
      const parts = [];
      if (s.match) parts.push(s.match.title);
      parts.push(`${s.encoder} #${s.streamId}`);
      if (s.dest) parts.push(`→ ${s.dest.origin}/${s.dest.app}/${s.dest.channel}`);
      if (s.err) parts.push(`(calendar: ${s.expected.origin}/${s.expected.app}/${s.expected.channel})`);
      return parts.join(' ');
    },
    oriSub(o) {
      if (!o.items.length) return { text: 'Idle', bad: false };
      const arriving = o.items.filter(it => it.type === 'in' && it.s.running).length;
      const issues = o.items.filter(it => it.type === 'exp' || it.s.err || it.s.kind === 'down').length;
      let text = `${arriving} ${arriving === 1 ? 'channel' : 'channels'} arriving`;
      if (issues) text += ` · ${issues} ${issues === 1 ? 'issue' : 'issues'}`;
      return { text, bad: issues > 0 };
    },
    originRowSub(it) {
      return `${it.s.encoder} #${it.s.streamId}`;
    },
    originRowLabel(it) {
      const s = it.s;
      if (it.type === 'exp') return 'Not arriving';
      switch (s.kind) {
        case 'live': return 'Live';
        case 'checking': return 'Checking';
        case 'mismatch': return s.err === 'origin' ? 'Wrong origin' : 'Wrong channel';
        case 'down': return 'Down';
        case 'unknown': return 'No status';
        default: return '';
      }
    }
  }
};
</script>
